import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import JsonLd, { SITE, ORG_ID, APPROVED_DESCRIPTION } from '../components/JsonLd';
import SEO from '../components/SEO';
import {
  Instagram,
  Globe,
  Mail,
  Youtube,
  ChevronDown,
} from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import PathwayGateway from '../components/PathwayGateway';
import '../styles/HomeV2.css';
import '../styles/RainbowText.css';
import '../styles/Brochure.css';
import '../styles/Subpage.css';
import '../styles/EmotionalPositioning.css';

// Nav is delegated to <SiteHeader/> — kept identical across the entire site.

const PosterCopy = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="home-poster-copy" data-testid="home-poster-copy">
      <p className="home-poster-lede">Neuro-inclusive family access, embedded into the built environment.</p>
      <p className="home-poster-body" id="home-poster-more" hidden={!open} data-testid="home-poster-body">
        N3URO WORLD® works across sectors to help organisations consider neurological difference within the
        environments, systems and experiences they create &mdash; from individual spaces and developments to wider
        infrastructure and organisational programmes. We support the organisations responsible for shaping the
        built environment through neuro-inclusive family infrastructure.
      </p>
      <button type="button" className="home-read-more" aria-expanded={open} aria-controls="home-poster-more" onClick={() => setOpen((o) => !o)} data-testid="home-read-more-btn">
        {open ? 'Show less' : 'Read more'} <ChevronDown size={14} strokeWidth={2.4} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
      </button>
    </div>
  );
};

const LandingPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="home2-page" data-testid="home-v2-page">
      <SEO
        title="N3URO WORLD® | Neuro-Inclusive Family Infrastructure"
        description="N3URO WORLD® is a neuro-inclusive family infrastructure platform bringing together shared language, practical family-access tools, membership pathways and organisational engagement."
        path="/"
      />
      <JsonLd data={[
        { '@type': 'Organization', '@id': ORG_ID, name: 'N3URO WORLD®', legalName: 'N3URO WORLD LTD', url: SITE, logo: `${SITE}/img/n3uro-world-logo-square.png`, description: APPROVED_DESCRIPTION, email: 'hello@n3uro.world', sameAs: ['https://www.instagram.com/n3uro_world'] },
        { '@type': 'WebSite', '@id': `${SITE}/#website`, name: 'N3URO WORLD®', url: SITE, description: APPROVED_DESCRIPTION, publisher: { '@id': ORG_ID } },
      ]} />

      {/* ============= HEADER (shared across the entire site) ============= */}
      <SiteHeader currentPath="/" />

      {/* ============= HOME HERO — image AT TOP followed by the standard
          .subpage-title (rainbow gradient) + .subpage-lede (white)
          pattern used on every page for a consistent voice. */}
      <section className="brochure-page" id="home-mission">
        <figure className="home-poster" data-testid="home-poster">
          <picture>
            <source media="(min-width: 768px)" srcSet="/img/home-hero-wide.webp" width="1920" height="960" />
            <img
              src="/img/home-hero-r.webp"
              width="1024"
              height="1536"
              alt="N3URO WORLD® — Access Built Differently. Neuro-Inclusive Family Infrastructure. A glowing planet at dusk with connected light points across continents, and the N3URO WORLD® wordmark above."
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </picture>
        </figure>

        <section className="home-hero" aria-labelledby="home-hero-heading" data-testid="home-hero">
          <h1 id="home-hero-heading" className="subpage-title home-hero-title">
            Developing Neuro-Inclusive Family Infrastructure Across the Built
            Environment
          </h1>
        </section>

        <div className="brochure-wrap">
          <PosterCopy />
        </div>

        <PathwayGateway />
      </section>

      {/* ============= EMOTIONAL POSITIONING + POSITIONING STATEMENT ============= */}
      <section
        className="emo-band"
        id="emotional-positioning"
        data-testid="home-emotional-positioning"
      >
        <div className="emo-band-inner">
          <div>
            <h2 className="emo-heading emo-heading--brand">
              N3URO WORLD®
            </h2>
            <p className="emo-heading-sub">
              Access &mdash; Built &mdash; Differently
            </p>
          </div>

          <div className="emo-position" data-testid="home-positioning-statement">
            <span className="emo-position-label">The N3URO WORLD® position</span>
            <p className="emo-position-body">
              N3URO WORLD® is a{' '}
              <em>Neuro-Inclusive Family Infrastructure</em> designed around
              family access, participation, regulation and connection.
            </p>
          </div>
        </div>
      </section>

      {/* anchor target so legacy /#home-contact and /#home-paths still scroll near the bottom */}
      <span id="home-paths" />
      <span id="home-contact" />

      {/* ============= FOOTER ============= */}
      <footer className="home2-footer">
        <div className="home2-footer-grid">
          <div className="home2-footer-brand">
            <Link to="/" className="home2-footer-logo">
              <img width="600" height="180" loading="lazy" decoding="async"
                src="/img/n3uro-world-logo-wide.webp"
                alt="N3URO WORLD®"
              />
            </Link>
            <p>
              Neuro-inclusive family infrastructure.<br />
              Different minds. Bright futures.<br />
              Stronger together.
            </p>
          </div>

          <div className="home2-footer-col">
            <h4>Quick Links</h4>
            <Link to="/about">About Us</Link>
            <Link to="/advisory">N3URO Advisory</Link>
          </div>

          <div className="home2-footer-col">
            <h4>More</h4>
            <Link to="/developers-partners">Developers &amp; Partners</Link>
            <Link to="/knowledge">N3URO Knowledge</Link>
            <Link to="/impact">Impact</Link>
            <button type="button" onClick={() => scrollTo('home-paths')} className="home2-footer-linklike">FAQ</button>
            <Link to="/contact">Contact</Link>
          </div>

          <div className="home2-footer-col">
            <h4>Connect With Us</h4>
            <a href="https://instagram.com/n3uro_world" target="_blank" rel="noopener noreferrer">
              <Instagram size={15} /> @n3uro_world
            </a>
            <a href="https://n3uro.world" target="_blank" rel="noopener noreferrer">
              <Globe size={15} /> www.n3uro.world
            </a>
            <a href="mailto:hello@n3uro.world">
              <Mail size={15} /> hello@n3uro.world
            </a>
          </div>

          <div className="home2-footer-col">
            <h4>Stay Connected</h4>
            <p className="home2-footer-tagline">
              Follow our journey and be part of a community building a better future for every
              mind.
            </p>
            <div className="home2-footer-socials">
              <a href="https://instagram.com/n3uro_world" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><Instagram size={16} /></a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><Youtube size={16} /></a>
            </div>
          </div>
        </div>

        <div className="home2-footer-base">
          <span>&copy; 2026 N3URO WORLD<sup>®</sup>. All rights reserved.</span>
          <address className="site-footer-address" data-testid="footer-address">71-75 Shelton Street, Covent Garden, London, United Kingdom, WC2H 9JQ</address>
          <span className="home2-footer-base-links">
            <Link to="/what-is-n3uro-world" data-testid="home-footer-what-is">What is N3URO WORLD®?</Link>
            <span aria-hidden="true">·</span>
            <a href="https://www.instagram.com/n3uro_world" target="_blank" rel="me noopener noreferrer" data-testid="home-footer-instagram">Instagram</a>
            <span aria-hidden="true">·</span>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms">Terms &amp; Conditions</Link>
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
